import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ArrowUp, ArrowDown, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import api, { resolveMedia } from "@/lib/api";

/**
 * Gestione galleria multipla (prodotti e blog).
 * items: [{url, alt, caption?, show_in_body?}] — coverUrl: url corrente di copertina.
 * withBlogFields: mostra didascalia + interruttore "mostra nel testo".
 */
export function GalleryManager({ items, onChange, coverUrl, onCover, withBlogFields = false, testId = "gallery" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const set = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const arr = items.slice();
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const remove = (i) => onChange(items.filter((_, j) => j !== i));

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ url: data.url, alt: "", caption: "", show_in_body: true });
      }
      onChange([...items, ...added]);
      toast.success(`${added.length} immagin${added.length === 1 ? "e caricata" : "i caricate"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-taupe">{items.length} immagin{items.length === 1 ? "e" : "i"} · trascina l'ordine con le frecce</span>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
          className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase border border-ink px-4 hover:bg-ink hover:text-bone transition-colors disabled:opacity-60"
          data-testid={`${testId}-upload`}>
          <Upload size={14} /> {busy ? "Caricamento…" : "Aggiungi immagini"}
        </button>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" multiple className="hidden" data-testid={`${testId}-file`}
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
      </div>

      <div className="mt-3 space-y-3">
        {items.map((it, i) => (
          <div key={it.url + i} className="border border-hair bg-white p-3 flex gap-3 items-start" data-testid={`${testId}-item-${i}`}>
            <img src={resolveMedia(it.url)} alt={it.alt || ""} className="w-20 h-20 object-cover shrink-0 border border-hair" />
            <div className="flex-1 min-w-0 space-y-2">
              <Input value={it.alt || ""} onChange={(e) => set(i, { alt: e.target.value })} placeholder="Testo alternativo (SEO/accessibilità)"
                className="rounded-none h-9 text-xs" data-testid={`${testId}-alt-${i}`} />
              {withBlogFields && (
                <>
                  <Input value={it.caption || ""} onChange={(e) => set(i, { caption: e.target.value })} placeholder="Didascalia (opzionale)"
                    className="rounded-none h-9 text-xs" data-testid={`${testId}-caption-${i}`} />
                  <label className="flex items-center gap-2 text-xs text-taupe cursor-pointer min-h-[32px]">
                    <Switch checked={it.show_in_body !== false} onCheckedChange={(v) => set(i, { show_in_body: v })} data-testid={`${testId}-show-${i}`} />
                    Mostra nell'articolo
                  </label>
                </>
              )}
              {coverUrl === it.url && <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase text-gold"><Star size={11} fill="currentColor" /> Copertina</span>}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {onCover && coverUrl !== it.url && (
                <button type="button" onClick={() => onCover(it.url)} className="p-2 border border-hair hover:border-gold hover:text-gold" title="Imposta come copertina" data-testid={`${testId}-cover-${i}`}><Star size={14} /></button>
              )}
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-2 border border-hair hover:border-ink disabled:opacity-30" title="Sposta su" data-testid={`${testId}-up-${i}`}><ArrowUp size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 border border-hair hover:border-ink disabled:opacity-30" title="Sposta giù" data-testid={`${testId}-down-${i}`}><ArrowDown size={14} /></button>
              <button type="button" onClick={() => remove(i)} className="p-2 border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone" title="Elimina immagine" data-testid={`${testId}-del-${i}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-taupe py-4 text-center border border-dashed border-hair">Nessuna immagine in galleria</p>}
      </div>
    </div>
  );
}
